// Locally used components
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Footer,
  Heading,
  Link,
  ProgressCircle,
  Provider as ProviderV3,
  Radio,
  RadioGroup,
  Text,
  TextArea,
  TextField,
  defaultTheme,
} from '@adobe/react-spectrum';
import Provider from '@react/react-spectrum/Provider';
import { success } from '@react/react-spectrum/Toast';
import qs from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { useIMS } from '@exchange-frontends/custom-hooks';

import HelpSupportGridListItem from './HelpSupportGridListItem';

/**
 * main details container
 * @memberof ReportAbuse
 * @type {styled-component}
 */

const RedText = styled.span`
  color: red;
`;

export const MESSAGES = {
  description: {
    empty: 'Enter description.',
    notValid: 'Invalid format: this field accepts a max number of 1000 characters',
  },
  radioGroup: {
    error: 'Select an option.',
  },
};

const FormContainer = styled.form`
  margin-top: 30px;
  &&&& > div:not(.spectrum-FieldGroup) {
    display: grid;
    grid-template-columns: 3fr;
    margin: 15px 0;
    width: 100%;
  }
`;

const INITIAL_FORM_VALUES = {
  email: { value: '', isRequired: true },
  name: { value: '', isRequired: false },
  description: { value: '', isRequired: true },
  category: { value: '', isRequired: true },
};

const ReportAbuse = ({ label, icon, loading, appType }) => {
  const navigate = useNavigate();
  const search = useLocation().search;
  const { id: appId } = useParams();
  const section = qs.parse(search).section;
  const isDialogOpen = section === 'report-abuse';
  const [sendingReportAbuseErrored, setSendingReportAbuseErrored] = useState(false);
  const [sendingReportAbuseLoading, setSendingReportAbuseLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM_VALUES);
  const ims = useIMS();
  const profileLoading = useMemo(() => ims?.isMock || !ims?.isSignedInUser(), [ims]);
  const isRadioGroupInvalid = form.category.valid === 'invalid';

  const setIsDialogOpen = (newVal) => {
    if (newVal) {
      navigate({ search: qs.stringify({ section: 'report-abuse' }) }, { replace: true });
    } else {
      navigate({ search: '' }, { replace: true });
    }
  };
  const validateField = (key) => {
    if (form[key].isRequired && !form[key].value) {
      return 'invalid';
    } else if (key === 'category') {
      return form[key].value?.length < 1000 ? 'valid' : 'invalid';
    } else if (form[key].isRequired) {
      return 'valid';
    } else {
      return undefined;
    }
  };

  const validateForm = () => {
    let result = true;
    form.category.value = category;
    const newForm = { ...form };
    Object.keys(form).forEach((key) => {
      newForm[key].valid = validateField(key);
      if (newForm[key].valid === 'invalid') {
        result = false;
      }
    });
    setForm(newForm);
    return result;
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setSendingReportAbuseErrored(false);
    setSendingReportAbuseLoading(false);
    setCategory(null);
    setForm((prev) => ({
      ...prev,
      email: { value: '', isRequired: true },
      name: { value: '', isRequired: false },
      description: { value: '', isRequired: true },
      category: { value: '', isRequired: true },
    }));
  };

  const onFormChange = (event) => {
    setForm((prev) => {
      const newState = { ...prev };
      newState[event.target.id].value = event.target.value;
      newState[event.target.id].valid = validateField(event.target.id);
      return newState;
    });
  };

  // pre-fill email/name with ims profile
  // otherwise redirect to sign in
  useEffect(() => {
    let mounted = true;
    const getProfileForForm = async () => {
      if (isDialogOpen) {
        if (!ims?.isMock && ims?.initialized && ims?.isSignedInUser()) {
          const { email, name } = await ims.getProfile();
          if (mounted) {
            setForm((prev) => {
              const newState = { ...prev };
              newState.email.value = email;
              newState.name.value = name;
              return newState;
            });
          }
        } else {
          ims.signIn();
        }
      }
    };
    getProfileForForm();
    return () => {
      mounted = false;
    };
  }, [ims, isDialogOpen]);

  const sendReportAbuse = async () => {
    if (validateForm()) {
      setSendingReportAbuseLoading(true);
      setSendingReportAbuseErrored(false);
      try {
        const accessToken = ims.getAccessToken().token;
        const handler = handleApiCall(excApi.sendReportAbuse, {
          accessToken,
          appId,
          appType,
          description: form.description.value,
          category: form.category.value,
        });
        await handler.run();
        success('Email was successfully sent');
        resetForm();
        setIsDialogOpen(false);
      } catch (err) {
        console.log(err);
        setSendingReportAbuseErrored(true);
      } finally {
        setSendingReportAbuseLoading(false);
      }
    }
  };

  return (
    <>
      <HelpSupportGridListItem
        icon={icon}
        label={label}
        loading={loading}
        dataLaunchId={`Help and Support : Report abuse`}
        onClick={() => setIsDialogOpen(true)}
      />
      <DialogContainer>
        {isDialogOpen && (
          <Dialog>
            <Heading level={2} marginBottom="size-50" marginTop="size-0">
              {label}
            </Heading>
            <Divider size="M" marginBottom="size-300" />

            <ButtonGroup>
              <Button variant="secondary" onPress={resetForm}>
                Cancel
              </Button>
              <Button
                autoFocus
                variant="cta"
                onPress={sendReportAbuse}
                isDisabled={profileLoading || sendingReportAbuseLoading}
              >
                {sendingReportAbuseLoading && (
                  <ProgressCircle
                    isIndeterminate
                    size="S"
                    variant="overBackground"
                    aria-label="sending-report-abuse-progress"
                    marginEnd="5px"
                  />
                )}
                <Text>Report</Text>
              </Button>
            </ButtonGroup>

            <Content>
              <Text>
                If you feel that this product violates the Adobe
                <Link marginStart="size-40" variant="secondary">
                  <a href="https://www.adobe.com/legal/terms.html" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>
                </Link>
                , you may report this product by filling out this quick form.
              </Text>
              <ProviderV3 theme={defaultTheme} colorScheme={`light`}>
                <Provider toastPlacement="bottom center" theme="light">
                  <FormContainer onChange={onFormChange}>
                    <TextField
                      label="Your Email"
                      required
                      type="email"
                      id="email"
                      isDisabled={profileLoading}
                      isRequired={form.email.isRequired}
                      value={form.email.value}
                      isReadOnly
                      labelPosition="top"
                    />
                    <TextField
                      label="Your Name"
                      marginTop="size-250"
                      type="text"
                      id="name"
                      isRequired={form.name.isRequired}
                      isDisabled={profileLoading}
                      value={form.name.value}
                      isReadOnly
                    />
                    <RadioGroup
                      label="Why are you reporting this product?"
                      orientation="horizontal"
                      isRequired={form.category.isRequired}
                      necessityIndicator="icon"
                      isInvalid={isRadioGroupInvalid}
                      value={category}
                      onChange={setCategory}
                      isEmphasized="true"
                      errorMessage={MESSAGES.radioGroup.error}
                    >
                      <Radio value="DEFAMATION" width="200px">
                        Defamation
                      </Radio>
                      <Radio value="TI" width="200px">
                        Trademark Infringement
                      </Radio>
                      <Radio value="OC" width="200px">
                        Offensive Content
                      </Radio>
                      <Radio value="RHC" width="200px">
                        Racist or Hate Content
                      </Radio>
                      <Radio value="SEC" width="200px">
                        Sexually Explicit Content
                      </Radio>
                      <Radio value="IR" width="200px">
                        Issue with a review
                      </Radio>
                    </RadioGroup>

                    <TextArea
                      label="Please provide a description of your concern."
                      placeholder="Provide a description"
                      marginTop="size-200"
                      isRequired={form.description.isRequired}
                      value={form.description.value}
                      errorMessage={form.description.value ? MESSAGES.description.notValid : MESSAGES.description.empty}
                      validationState={form.description.valid}
                      type="text"
                      id="description"
                      isDisabled={profileLoading}
                    />
                  </FormContainer>
                </Provider>
              </ProviderV3>
              <Text>
                To report a Copyright Violation, please follow Section 19 in the
                <Link marginStart="size-40" variant="secondary">
                  <a href="https://www.adobe.com/legal/terms.html" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>
                </Link>
              </Text>
            </Content>
            <Footer>{sendingReportAbuseErrored && <RedText>Failed to send Report Abuse</RedText>}</Footer>
          </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default ReportAbuse;
